import React, { type ChangeEvent, type ReactElement, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { debounce } from 'lodash';
import { useController, useFormContext } from 'react-hook-form';
import type { FieldPath, FieldValues, Path } from 'react-hook-form';
import AppliedFilters from 'atom-components/applied-filters-lite';
import Label from 'atom-components/label';
import { defaultDebounceMilliseconds } from './helpers';
import NumberInput from './number-input';
import { type SharedNumberProps } from './types';

const fromLabel = 'Between';
const toLabel = 'and';

type NumberRangeProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  // We have to pass fromName and toName seperately to ensure that the field names are type-safe.
  // Passing a string prefix and combining the From and To field names dynamically is not possible.
  fromName: Path<TFieldValues>;
  toName: Path<TFieldValues>;
  ariaLabel: string; // The text of the range <Label> that will have "From" or "To" appended to it.
} & SharedNumberProps<TFieldValues, TName>;

/**
 * A controlled number range, represented by two number inputs. By convention the form fields should
 * end in "From" and "To"; these words are appended to the id and ariaLabel of the specific number
 * inputs automatically to avoid duplication and enforce consistency.
 */
export default function NumberRange<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  fromName,
  toName,
  ariaLabel,
  className,
  min,
  max,
  disabled,
  onChange,
  debounceOnChange,
  debounceMilliseconds = defaultDebounceMilliseconds
}: NumberRangeProps<TFieldValues, TName>): ReactElement {
  const { field: fromField } = useController({ name: fromName });
  const { field: toField } = useController({ name: toName });

  const onChangeDebounced = useRef(
    debounce((e: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      }
    }, debounceMilliseconds)
  ).current;
  useEffect(() => () => onChangeDebounced.cancel(), [onChangeDebounced]);

  const onEitherInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (onChange) {
      if (debounceOnChange) {
        onChangeDebounced(e);
      } else {
        onChange(e);
      }
    }
  };

  return (
    <div className={clsx('row g-3 align-items-center', className)}>
      <div className="col-auto pt-3">
        <Label htmlFor={`${id}From`}>{fromLabel}</Label>
      </div>
      <div className="col-auto">
        <NumberInput
          id={`${id}From`}
          name={fromName}
          ariaLabel={`${ariaLabel} From`}
          min={min}
          max={max}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            fromField.onChange(e);
            onEitherInputChange(e);
          }}
          debounceOnChange
        />
      </div>
      <div className="col-auto pt-3">
        <Label htmlFor={`${id}To`}>{toLabel}</Label>
      </div>
      <div className="col-auto">
        <NumberInput
          id={`${id}To`}
          name={toName}
          ariaLabel={`${ariaLabel} To`}
          min={min}
          max={max}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            toField.onChange(e);
            onEitherInputChange(e);
          }}
          debounceOnChange
        />
      </div>
    </div>
  );
}

type AppliedNumberRangeProps<TFieldValues extends FieldValues = FieldValues> = {
  rangeLabel: string;
  fromName: Path<TFieldValues>;
  toName: Path<TFieldValues>;
  defaultFromValue: string;
  defaultToValue: string;
  onClear: () => void;
};

export function AppliedNumberRange({
  rangeLabel,
  fromName,
  toName,
  defaultFromValue,
  defaultToValue,
  onClear
}: AppliedNumberRangeProps): ReactElement | null {
  const { watch, resetField, formState } = useFormContext();
  const watchedFromField = watch(fromName);
  const watchedToField = watch(toName);

  if (
    (watchedFromField === defaultFromValue && watchedToField === defaultToValue) ||
    formState.errors[fromName] ||
    formState.errors[toName]
  )
    return null;

  return (
    <AppliedFilters.Filter name={rangeLabel}>
      <AppliedFilters.FilterItem
        onClick={(): void => {
          resetField(fromName, { defaultValue: defaultFromValue });
          resetField(toName, { defaultValue: defaultToValue });
          onClear();
        }}
      >
        {fromLabel} {watchedFromField} {toLabel} {watchedToField}
      </AppliedFilters.FilterItem>
    </AppliedFilters.Filter>
  );
}
